// ============================================================================
// /100 — "100 by Aluume" founder-membership sales page: ALL copy + figures.
//
// Everything editable lives here so Jeremy / Tim can correct wording or numbers
// without touching component code. Ported verbatim from the signed-off mockup
// (Staff Documents/10 Website/100 By Aluume/100-by-aluume-site/index.html),
// with two deliberate departures per the approved card decisions:
//   • Direct-charge model — the page takes the real $3,000 payment. The mockup's
//     "free 14-day hold / book a call first" framing is dropped; the reserve
//     steps + refund FAQ are rewritten around a 14-day cooling-off refund.
//   • Real design system tokens — no Kepler/Hanken. Type is page-specific:
//     Libre Baskerville + Inconsolata, wired in ./fonts.ts (owner, 2026-09-02).
//
// PLACEHOLDERS that must be confirmed before this page is PUBLISHED (build is
// unblocked — these are flagged, not gates):
//   • CALCULATOR.casualRate — invented drop-in rate; money-facing.
// The opening date is server-driven from venues.opens_on (Sessions Setup), read at runtime
// via /api/membership/opening — no date is hard-coded here.
// The FAQ answers have now been reconciled against the published Thermae100
// Membership Terms at /termsThermae100 (app/termsThermae100/content.ts), so
// none carry `flag: true` any longer.
// ============================================================================

export const CONFIG = {
  membershipsTotal: 100,
  priceMembership: 3000, // NZD, inc GST, one-time buy-in
  founderDaily: 9.04, // NZD/day equivalent across founder windows (signed-off Prospectus figure)
  openingLabel: "Q4 2026",
  contactEmail: "thermae@aluume.com",
  // Thermae100 membership Terms (NOT the site-wide /terms booking terms) — the
  // /100 checkout acceptance checkbox + the Reserve section link here.
  termsUrl: "/termsThermae100",
};

// Scarcity label shared by the hero badge, sticky mobile CTA and checkout panel.
// While nothing is sold (remaining === total, or still loading) show the plain
// "N memberships available"; once any are gone switch to the "N of 100 remaining"
// countdown treatment.
export function scarcityLabel(remaining: number | null, total: number): string {
  if (remaining !== null && remaining < total) return `${remaining} of ${total} remaining`;
  return `${total} memberships available`;
}

// ── Hero ────────────────────────────────────────────────────────────────────
// The buy-in / term / opening stat row was removed per review — the hero is just
// the badge, headline, sub-line and the single "Reserve your place" CTA. Those
// facts now live in the "Your founder membership" section below.
export const HERO = {
  // Static hero badge — a plain "Only 100 available" pill (per review, the hero no
  // longer shows the live "N of 100 remaining" count; scarcity still drives the
  // sticky CTA + checkout panel).
  badge: "Only 100 available",
  headline: "Hot, cold, lake,",
  headlineEm: "repeat.",
  sub: "100 founder memberships for Thermae, a floating sauna on Lake Wakatipu, Queenstown. Opening Q4 2026.",
  // Hero is a single static image — the angled exterior-dusk shot (no crossfade),
  // slowly panned (Ken Burns) so it feels alive without a video.
  image: "/100/thermae-exterior-dusk",
};

// ── Your founder membership (price + fact tiles + calculator) ─────────────────
// The buy-in is framed as a monthly equivalent ($3,000 ÷ 12 = $250/month) with
// the one-time figure as subtext. A $250/month price tile sits in one even
// three-across row with two at-a-glance fact tiles (the "The offer" + "Opens"
// tiles were removed per review), followed by the "how often will you come?"
// value calculator (slider only, 1–5 sessions a week).
export const GLANCE = {
  eyebrow: "Your founder membership",
  heading: "Exclusive membership for our first 100 founders.",
  intro:
    "One hundred founder memberships. Yours for twelve months of unlimited* access from the day we open. First Light, Reset and Late Night, every day.",
  price: {
    amount: "$250",
    unit: "/ month",
    sub: "$3,000 one time, inc. GST.",
  },
  // Two at-a-glance fact tiles shown in an even three-across row with the price
  // tile. The buy-in figure lives in the price sub above; the "The offer" and
  // "Opens" tiles were dropped per review to keep the row to three even tiles.
  // The Access tile's "Unlimited" value carries a trailing "*" — the deliberate
  // marketing headline (owner, 2026-08-29). That "*" is qualified on the page by
  // the visible footnote ("*See Membership Terms.") the Glance section renders
  // directly beneath these tiles. The one-session-per-day rule is kept off the
  // /100 sales page (owner, 2026-08-29) and lives only in /termsThermae100. Do
  // NOT drop the "*". (The Inclusions grid that used to restate this claim was
  // replaced by the film section on 2026-09-03, so this is now the ONLY place the
  // unlimited-access claim is made on /100 — which makes the asterisk and its
  // footnote load-bearing rather than one of two.)
  items: [
    { eyebrow: "Access", value: "Unlimited*", sub: "First Light, Reset & Late Night, every day." },
    { eyebrow: "Your term", value: "12 months", sub: "From the day the doors open." },
  ],
};

// ── The place — single-image hotspot showcase ─────────────────────────────────
export type Hotspot = { top: string; left: string; eyebrow: string; body: string };

export const SHOWCASE = {
  heading: "Designed to move with the water, not against it.",
  intro:
    "Three saunas. A steam room. An alpine lake cold plunge. Built off-site, floated into position at Queenstown Marina. Tap a pin to look closer.",
  // Single static image — the front-on dock-entrance shot, with all five hotspots
  // merged onto it. Placement is a first pass; exact positioning to be directed.
  image: "/100/thermae-dock-entrance",
  alt: "Thermae entrance deck at dawn, timber and glass pavilion with lake pools, The Remarkables beyond",
  hotspots: [
    { top: "26%", left: "20%", eyebrow: "The Remarkables view", body: "Glass-fronted toward the range. Steam rising, mountains holding still." },
    { top: "44%", left: "60%", eyebrow: "Private sauna", body: "Seats six, with its own cold plunge. Book it for your family, your friends, or an hour to yourself." },
    { top: "50%", left: "40%", eyebrow: "Two communal saunas", body: "Ten seats and four, both stepping straight into the lake. Room to bathe with others, or find a quiet corner." },
    { top: "36%", left: "80%", eyebrow: "Communal steam room", body: "Soft, wet heat beside the dry saunas. A gentler way to warm through between plunges." },
    { top: "66%", left: "12%", eyebrow: "Cold plunge steps", body: "Straight off the timber deck into Lake Wakatipu. No chiller, no chemicals. The lake itself is the plunge." },
    { top: "72%", left: "66%", eyebrow: "Floated into position", body: "Built off-site, floated into position at Queenstown Marina. Five minutes from the airport, closer from the water." },
  ] as Hotspot[],
};

// ── The daily ritual — time-of-day explorer ───────────────────────────────────
export type RitualState = {
  tab: string;
  time: string;
  note: string;
  quote: string;
  desc: string;
};

export const RITUAL = {
  eyebrow: "The daily ritual",
  heading: "Yours at first light, reset & last light.",
  states: [
    {
      tab: "First Light",
      time: "07:00-09:00",
      note: "Dawn stillness",
      quote: "Glassy water at dawn. Steam rising off the water. Yours, every morning.",
      desc: "The lake at its stillest. First Light runs 07:00 to 09:00.",
    },
    {
      tab: "Reset",
      time: "11:00-14:00",
      note: "Midday reset",
      quote: "A midday quiet. Heat, plunge, lake. The kind of break that resets the day.",
      desc: "Bright and clear. Reset runs 11:00 to 14:00.",
    },
    {
      tab: "Late Night",
      time: "21:00-23:00",
      note: "Night steam",
      quote: "The day folds down. Heat, plunge, then home. Sleep arrives deeper.",
      desc: "Steam meeting cold at last light. Late Night runs 21:00 to 23:00.",
    },
  ] as RitualState[],
};

// ── Contrast therapy ──────────────────────────────────────────────────────────
export type ContrastPhase = {
  label: string;
  node: string;
  eyebrow: string;
  title: string;
  body: string;
  stats: { value: string; label: string }[];
};

export const CONTRAST = {
  eyebrow: "The practice",
  heading: "Hot, cold, lake, repeat.",
  intro: "Sauna and cold plunge, done properly, in one visit. Tap each phase for what's actually happening.",
  note: "General information only, not medical advice. Consult your doctor if you have any medical conditions or are unsure whether contrast therapy is right for you.",
  phases: [
    {
      label: "Heat", node: "85-95°", eyebrow: "Phase 01 · Sauna, 85-95°C",
      title: "The heart works, gently.",
      body: "Dry heat raises your core temperature and heart rate. The effect is similar to light cardiovascular exercise. Blood vessels dilate, circulation improves, and heat-shock proteins are triggered, which research suggests support cell repair.",
      stats: [{ value: "~15-20 min", label: "Typical sauna round" }, { value: "85-95°C", label: "Sauna temperature" }],
    },
    {
      label: "Cold plunge", node: "Lake", eyebrow: "Phase 02 · The lake itself",
      title: "Lake Wakatipu is the plunge.",
      body: "No chiller, no chemicals. Straight off the deck into alpine lake water. Cold exposure has been linked in research to lower heart rate and cortisol, reduced inflammation, and a lift in mood that studies suggest can last for hours.",
      stats: [{ value: "~1-3 min", label: "Typical plunge" }, { value: "Alpine lake", label: "Lake Wakatipu" }],
    },
    {
      label: "Rest", node: "Rest", eyebrow: "Phase 03 · Rest",
      title: "The part that's easy to skip.",
      body: "Rest lets your nervous system settle between rounds. This is where the parasympathetic (“rest and digest”) response takes over. It is the same state associated in research with better sleep and steadier mood.",
      stats: [{ value: "5-10 min", label: "Typical rest" }, { value: "On the deck", label: "Or by the water" }],
    },
    {
      label: "Repeat", node: "Repeat", eyebrow: "Phase 04 · Repeat",
      title: "Two to three rounds.",
      body: "The practice is in the repetition. Most visits move through heat, cold and rest two to three times. Consistency over months is where the research on cardiovascular and cognitive benefits is strongest.",
      stats: [{ value: "2-3 rounds", label: "Typical visit" }, { value: "45-75 min", label: "Total visit time" }],
    },
  ] as ContrastPhase[],
};

// ── Proven in Queenstown ──────────────────────────────────────────────────────
export type ProvenStat = { value: string; label: string; note?: string; image?: string };

export const PROVEN = {
  eyebrow: "Proven in Queenstown",
  heading: "The team behind Queenstown's award-winning hot pools.",
  intro:
    "Thermae is the second Aluume venue, not a first bet. Bathe by Aluume opened November 2025 on Brecon Street, Queenstown. And Queenstown noticed.",
  stats: [
    // This tile carried a `note` naming the architect ("Patterson Associates, the
    // same architect designing Thermae."). Removed at the owner's request
    // (2026-09-02). The whole claim was that one sentence, so the note is dropped
    // outright rather than reworded — the tile reads as the award alone and needs
    // no supporting line. `note` stays on the type (and in the renderer) for any
    // future stat that wants one; nothing sets it today.
    { value: "Winner", label: "NZIA Southern Architecture Award 2026", image: "/100/ra26-southern-award-winner.webp" },
    { value: "4.9★", label: "Across 1,000+ Google reviews" },
    { value: "10,000+", label: "Guests in the first summer season" },
  ] as ProvenStat[],
};

// ── The film ──────────────────────────────────────────────────────────────────
// Replaced the "On the house" inclusions grid (owner, 2026-09-03) with the
// fly-through already running on aluume.com.
//
// ⚠️ WHAT LEFT THE PAGE WITH THAT GRID: "Unlimited founder hours", "Towels &
// amenities", "First option on what's next" and "A seat saved". The unlimited
// claim survives in GLANCE ("Unlimited*" / "First Light, Reset & Late Night,
// every day" / "12 months"), but towels & robes, first-option and seat-saved are
// now stated NOWHERE on /100 — and towels/robes are a contractual inclusion
// under clause 5 of the Thermae100 Membership Terms. Flagged to the owner; if
// they should stay on the sales page they need a home in GLANCE or the FAQ.
// Runs full bleed with no eyebrow or heading (owner, 2026-09-03) — the film is
// the whole section, so there is no on-page copy here to edit.
// SELF-HOSTED, not a YouTube embed (owner, 2026-09-03). Three rounds of trying to
// suppress the embed's chrome failed — YouTube draws its own UI and modestbranding
// is deprecated — so the film is served from our own origin, where a <video> with
// no `controls` attribute simply has no chrome to hide.
// Encoded from "Staff Documents/03 Media/03 Renders & AI/Thermae First Look.mp4"
// (4K HEVC, 114 MiB) down to H.264 1600x900 CRF30, audio stripped, +faststart:
// 7.1 MiB. A VP9 WebM was measured at 10.9 MiB — bigger — so there is no second
// source. Poster is frame 0, so the section never flashes black.
export const FILM = {
  src: "/100/thermae-first-look.mp4",
  poster: "/100/thermae-first-look-poster.webp",
  // Labels the section and the player for screen readers; not shown visually.
  title: "Thermae first look: a fly-through of the floating sauna",
};

// ── Value calculator ──────────────────────────────────────────────────────────
export const CALCULATOR = {
  heading: "How often will you come?",
  intro: "Move the slider. We'll do the maths on what your founder year actually costs, per visit.",
  // Slider only (preset buttons removed per review); notched 1–5 sessions a week.
  min: 1,
  max: 5,
  default: 3,
  weeks: 52,
  // PLACEHOLDER — invented drop-in/casual rate. Money-facing (feeds the per-visit
  // saving). Must be confirmed before publish.
  casualRate: 65,
  casualIsPlaceholder: true,
};

// ── Map ───────────────────────────────────────────────────────────────────────
// A single embedded Google Map pinned at Thermae, Queenstown Marina, with the
// "getting here" copy kept as text alongside. A plain Google Maps embed doesn't
// natively support the mockup's here/family category toggle + legend, so that
// interaction is dropped — the family context (Bathe, town) lives in the text.
export const MAP = {
  eyebrow: "Find us",
  heading: "Queenstown Marina.",
  // No-API-key Google Maps embed, pinned on the Queenstown/Queenstown Marina place
  // marker (‑45.0179585, 168.7170831). Rendered de-saturated (no colour) per review,
  // zoomed out (z=12) per review so central Queenstown as well as Frankton is in frame.
  embedUrl: "https://www.google.com/maps?q=-45.0179585,168.7170831&z=12&output=embed",
  directions:
    "Thermae sits at Queenstown Marina on Lake Wakatipu, about five minutes from Queenstown Airport and roughly ten from central Queenstown. Bathe by Aluume, our original hot pools, is on Brecon Street in town.",
};

// ── FAQ ───────────────────────────────────────────────────────────────────────
// These answers have been reconciled against the published Thermae100 Membership
// Terms at /termsThermae100 (app/termsThermae100/content.ts) — delayed opening =
// clauses 3+7, refundability = clause 7, transferability = clause 8, bad weather =
// clauses 4+11, booking-within-windows = clause 4. The hold/refund answers reflect
// the direct-charge model (no free 14-day hold — a 14-day cooling-off refund on the
// buy-in instead), per the approved card decision. `flag` is retained on the type
// for future draft answers but no entry currently sets it.
export type Faq = { q: string; a: string; flag?: boolean };

export const FAQS: Faq[] = [
  {
    q: "What if opening is delayed?",
    a: "If Thermae's opening is delayed beyond Q4 2026, your twelve months of daily founder access still starts on the actual opening day. Your place is held, and we'll keep you updated as the date firms up. If we haven't offered a confirmed opening date within twelve months of your purchase, you can cancel and receive a full refund of your buy-in.",
    flag: false,
  },
  {
    q: "Is my buy-in refundable?",
    a: "You have fourteen days from the day you purchase to change your mind for a full refund of your $3,000 buy-in. No reason needed. After that cooling-off period, the buy-in is non-refundable. Nothing in these terms limits your rights under the Consumer Guarantees Act 1993 or the Fair Trading Act 1986.",
    flag: false,
  },
  {
    q: "Can I transfer or gift the membership?",
    a: "Membership is personal to you and isn't transferable, assignable or shareable. It isn't currently set up as a gift purchase. Talk to us if this is what you had in mind and we'll see what's possible.",
    flag: false,
  },
  {
    q: "Can I bring a guest?",
    a: "Yes. Guests are welcome alongside you at the prevailing public rate.",
    flag: false,
  },
  {
    q: "What happens in bad weather?",
    a: "Thermae is built for the Queenstown climate, but daily windows may shift by up to thirty minutes for operational reasons, including weather. If a closure runs longer than seven consecutive days within your founder term, we'll extend your term by the equivalent number of days.",
    flag: false,
  },
  {
    q: "How does booking work within the windows?",
    a: "Founders book each visit in their windows. Pick First Light, Reset or Late Night, and reserve your spot so we can hold your place. Booking your founder sessions is included at no extra cost. See the Thermae100 Membership Terms for the full access conditions.",
    flag: false,
  },
];

// ── Reserve (direct-charge) ───────────────────────────────────────────────────
// Rewritten from the mockup's "free hold / book a call first" framing — the page
// takes the real $3,000 now, protected by the 14-day cooling-off refund (see FAQ).
export const RESERVE = {
  eyebrow: "Secure your place",
  heading: "Reserve your place.",
  intro:
    "One of one hundred founder memberships, taken here and now. Your $3,000 buy-in is fully refundable for fourteen days. After that, your founder year is locked in.",
  steps: [
    { n: "01", title: "Your details", body: "Name, email, phone and billing address. A couple of minutes." },
    { n: "02", title: "Pay securely", body: "$3,000 NZD, inc. GST, taken once through our secure card checkout." },
    { n: "03", title: "You're a founder", body: "Instant confirmation by email. Fourteen days to change your mind for a full refund." },
  ],
};
